/**
 * Spline math for the `spline` drafting entity (Faz 7.2 — OCS spline.rs
 * parity). The entity data bag stores the parametric source of truth:
 *
 *   { fitPoints?, controlPoints?, degree, knots?, knotParameterization,
 *     closed, tangentStart?, tangentEnd?, weights?, tessellation }
 *
 * - Fit mode (SPLINE): the fit points are interpolated with a global
 *   collocation solve (same algorithm as the kernel interpolateCurve) with
 *   the OCS KNOTS parameterization choice: Chord (0), Sqrt (1), Uniform (2).
 * - CV mode (SPLINECV): the control polygon IS the curve
 *   (curveFromControlPoints).
 *
 * `tessellation` is the display/measure cache; every mutation rebuilds it
 * through `retessellateSplineData` so entityToPolyline, snapping, DIVIDE and
 * the DXF writer all see the same geometry.
 */

import { findSpan, basisFunctions } from '../geometry/nurbs/curve';
import { sampleCurve, curveFromControlPoints } from '../geometry/nurbs/curve';
import type { NurbsCurveData } from '../geometry/nurbs/types';
import type { Pt3 } from './geometryMath';

/** OCS KNOTS parameterization: 0 Chord, 1 Sqrt (chord^0.5), 2 Uniform. */
export type KnotParameterization = 0 | 1 | 2;

/** Canonical `spline` entity data shape (the store data bag). */
export interface SplineData {
    fitPoints?: Pt3[];
    controlPoints?: Pt3[];
    degree: number;
    knots?: number[];
    knotParameterization: KnotParameterization;
    closed: boolean;
    tangentStart?: Pt3;
    tangentEnd?: Pt3;
    weights?: number[];
    /** Display/measure cache — always present on committed entities. */
    tessellation: Pt3[];
    color?: string;
    [key: string]: unknown;
}

/** Parameter values from cumulative chord (or sqrt/uniform) lengths. */
function parametersFor(points: Pt3[], param: KnotParameterization, n: number, p: number): number[] {
    if (param === 2) {
        const out: number[] = [];
        for (let i = 0; i < n; i++) out.push((i / (n - 1)) * (n - p));
        return out;
    }
    const dist = [0];
    for (let i = 1; i < n; i++) {
        const d = Math.hypot(
            points[i][0] - points[i - 1][0],
            points[i][1] - points[i - 1][1],
            (points[i][2] ?? 0) - (points[i - 1][2] ?? 0)
        );
        dist.push(dist[i - 1] + (param === 1 ? Math.sqrt(Math.max(d, 0)) : d));
    }
    const total = dist[n - 1] || 1;
    return dist.map((x) => (x / total) * (n - p));
}

/** Gaussian elimination with partial pivoting; one rhs column per axis. */
function solveLinear(A: number[][], B: Pt3[]): Pt3[] | null {
    const n = A.length;
    if (n === 0 || B.length !== n) return null;
    const m = A.map((row, i) => [...row, B[i][0], B[i][1], B[i][2] ?? 0]);
    for (let col = 0; col < n; col++) {
        let piv = col;
        for (let r = col + 1; r < n; r++) if (Math.abs(m[r][col]) > Math.abs(m[piv][col])) piv = r;
        if (Math.abs(m[piv][col]) < 1e-12) return null;
        if (piv !== col) [m[col], m[piv]] = [m[piv], m[col]];
        for (let r = 0; r < n; r++) {
            if (r === col) continue;
            const f = m[r][col] / m[col][col];
            if (f === 0) continue;
            for (let c = col; c < n + 3; c++) m[r][c] -= f * m[col][c];
        }
    }
    return m.map((row) => [row[n], row[n + 1], row[n + 2]] as Pt3);
}

/**
 * Global interpolation through fit points (A9.1-A9.6) with the chosen
 * parameterization. Closed fit splines wrap the first point around so the
 * loop is geometrically closed (OCS periodic mode approximated honestly —
 * the wrap keeps the loop seam C0 rather than building a periodic knot
 * vector; documented in the phase report).
 */
export function interpolateFitPoints(
    fitPoints: Pt3[],
    param: KnotParameterization,
    degree = 3,
    closed = false
): NurbsCurveData | null {
    // Closed loops repeat the first point so the last segment lands on it.
    const pts = closed && fitPoints.length > 1 ? [...fitPoints, fitPoints[0]] : fitPoints;
    const n = pts.length;
    if (n < 2) return null;
    if (n === 2) return { degree: 1, knots: [0, 0, 1, 1], points: pts.map((q) => [...q] as Pt3) };
    const p = Math.max(1, Math.min(degree, n - 1));
    const tBar = parametersFor(pts, param, n, p);
    const knots = new Array(n + p + 1).fill(0);
    for (let j = 1; j <= n - p; j++) {
        let s = 0;
        for (let i = j; i <= j + p - 1; i++) s += tBar[i];
        knots[j + p] = s / p;
    }
    for (let i = 0; i <= p; i++) knots[i] = 0;
    for (let i = n + 1; i <= n + p; i++) knots[i] = n - p;
    const params = [0];
    for (let j = 1; j <= n - p - 1; j++) {
        let s = 0;
        for (let i = j; i <= j + p - 1; i++) s += knots[i + 1];
        params.push(s / p);
    }
    params.push(n - p);
    const M: number[][] = params.map((t) => {
        const tc = Math.min(t, knots[n] - 1e-9);
        const span = findSpan(n - 1, p, tc, knots);
        const N = basisFunctions(span, p, tc, knots);
        const row = new Array(n).fill(0);
        for (let j = 0; j <= p; j++) row[span - p + j] = N[j];
        return row;
    });
    const solved = solveLinear(M, pts);
    return {
        degree: p,
        knots,
        points: solved ?? pts.map((q) => [...q] as Pt3),
        closed,
    };
}

/** Density rule (Faz 7 risk note): at least 16 samples per fit corner. */
function splineSampleCount(data: Pick<SplineData, 'fitPoints' | 'controlPoints' | 'tessellation'>): number {
    const corners = data.controlPoints?.length ?? data.fitPoints?.length ?? 2;
    return Math.max(64, corners * 16);
}

/** Kernel-ready curve from the data bag: CV mode is direct, fit mode is
 *  interpolated on demand with the stored parameterization. */
export function splineKernelCurve(data: SplineData): NurbsCurveData | null {
    if (Array.isArray(data.controlPoints) && data.controlPoints.length >= 2) {
        const c = curveFromControlPoints(data.controlPoints, data.degree);
        return { ...c, closed: data.closed };
    }
    if (Array.isArray(data.fitPoints) && data.fitPoints.length >= 2) {
        return interpolateFitPoints(data.fitPoints, data.knotParameterization ?? 0, data.degree, data.closed);
    }
    return null;
}

/** Rebuild the tessellation cache from the parametric source. */
export function retessellateSplineData(data: SplineData): SplineData {
    const curve = splineKernelCurve(data);
    data.tessellation = curve ? sampleCurve(curve, splineSampleCount(data)) : [];
    return data;
}

/** Build a fresh spline data bag from either fit or CV input. */
export function makeSplineData(
    input: { fitPoints?: Pt3[]; controlPoints?: Pt3[] },
    opts: { degree?: number; knotParameterization?: KnotParameterization; closed?: boolean } = {}
): SplineData {
    const data: SplineData = {
        degree: Math.max(1, Math.min(3, opts.degree ?? 3)),
        knotParameterization: opts.knotParameterization ?? 0,
        closed: !!opts.closed,
        tessellation: [],
    };
    if (input.fitPoints && input.fitPoints.length >= 2) data.fitPoints = input.fitPoints;
    if (input.controlPoints && input.controlPoints.length >= 2) data.controlPoints = input.controlPoints;
    const curve = splineKernelCurve(data);
    if (curve) {
        // CV mode keeps the exact knot vector so the DXF writer can emit it.
        if (Array.isArray(data.controlPoints)) {
            data.knots = curve.knots;
            data.degree = curve.degree;
        }
    }
    return retessellateSplineData(data);
}

/**
 * REVERSE for the spline data bag (OCS reverse.rs): fit/control/weight
 * arrays reverse in place-order, the knot field MIRRORS its span
 * ([0,0,0,.25,1,1,1] → [0,0,0,.75,1,1,1]), end tangents swap and negate.
 */
export function reverseSplineData(data: SplineData): SplineData {
    const rev = <T,>(a: T[] | undefined): T[] | undefined =>
        a ? [...a].reverse() : undefined;
    const next: SplineData = { ...data };
    if (data.fitPoints) next.fitPoints = rev(data.fitPoints);
    if (data.controlPoints) next.controlPoints = rev(data.controlPoints);
    if (data.weights) next.weights = rev(data.weights);
    if (data.knots && data.knots.length > 0) {
        const k = data.knots;
        const first = k[0], last = k[k.length - 1];
        next.knots = k.map((_, i) => first + (last - k[k.length - 1 - i]));
    }
    if (data.tangentStart || data.tangentEnd) {
        const neg = (t: Pt3 | undefined): Pt3 | undefined =>
            t ? ([-t[0], -t[1], -(t[2] ?? 0)] as Pt3) : undefined;
        next.tangentStart = neg(data.tangentEnd);
        next.tangentEnd = neg(data.tangentStart);
    }
    next.tessellation = [...data.tessellation].reverse();
    return next;
}

/** Nearest array index to a world point (fit/CV editing). */
export function nearestPointIndex(points: Pt3[], p: Pt3): number {
    let best = 0, bd = Infinity;
    points.forEach((q, i) => {
        const d = Math.hypot(q[0] - p[0], q[1] - p[1], (q[2] ?? 0) - (p[2] ?? 0));
        if (d < bd) { bd = d; best = i; }
    });
    return best;
}
